import { useEffect, useRef, useCallback, useState } from "react";
import useFeedbacks from "../../store/Querys/useFeedbacks";
import Typography from "../Typography/Typography";
import Divider from "../Divider/Divider";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const SessionOverviewList = ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const { id: subUserId } = useParams<{ id: string }>();
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useFeedbacks(
    organizationId,
    subUserId
  );

  const navigate = useNavigate();
  const location = useLocation();

  const getQueryParams = () => {
    const urlParams = new URLSearchParams(location.search);
    const name = urlParams.get("name");
    const lastName = urlParams.get("lastName");
    return { name, lastName };
  };
  const { name, lastName } = getQueryParams();

  const formatDate = (dateStr: string): string => {
    const [day, month, year] = dateStr.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date
      .toLocaleDateString("en-US", {
        month: "long",
        day: "2-digit",
        year: "numeric",
      })
      .replace(",", " /");
  };

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(handleObserver);
    if (bottomRef.current) observerRef.current.observe(bottomRef.current);
    return () => observerRef.current?.disconnect();
  }, [handleObserver]);

  const feedbacks = data?.pages.flatMap((page) => page.feedbacks) ?? [];

  const validFeedbacks = feedbacks.filter(
    (student) =>
      student && typeof student.date === "string" && student.date.trim() !== ""
  );

  const [hovered, setHoveredRow] = useState<number | null>(null);
  return (
    <div className="bg-white rounded-20 border border-secondary p-24 max-lg:p-12 max-lg:pt-24 max-lg:order-last max-lg:mb-[100px]">
      <Typography type="Header3">Session overview</Typography>
      <Typography type="Body2" className="!text-[#868686] mt-8">
        Detailed overview of daily sessions.
      </Typography>
      <Divider className="!bg-[#EAEBF9]" />

      {validFeedbacks.length === 0 ? (
        <div>
          <Typography type="Body2" className="text-center mt-24">
            The list is empty
          </Typography>
          <Typography type="Body3" className="!text-[#868686] text-center">
            No data available.
          </Typography>
        </div>
      ) : (
        <table className="grid border-collapse w-full grid-cols-2 mt-24 rounded-2xl border border-secondary overflow-hidden max-lg:pb-[24px]">
          <thead className="contents">
            <tr className="contents">
              <th className="p-24 bg-[#F7F7FD] border-b border-secondary text-left">
                <Typography type="Body2">Date</Typography>
              </th>
              <th className="p-24 bg-[#F7F7FD] border-b border-secondary text-left">
                <Typography type="Body2">Duration</Typography>
              </th>
            </tr>
          </thead>
          <tbody className="contents">
            {validFeedbacks.map((student, index) => (
              <tr key={student.id} className="contents">
                <td
                  onMouseEnter={() => setHoveredRow(index)}
                  onMouseLeave={() => setHoveredRow(null)}
                  className={`p-24 ${
                    index !== validFeedbacks.length - 1
                      ? "border-b border-secondary"
                      : ""
                  } cursor-pointer transition-colors duration-200 ease-in ${
                    hovered === index ? "bg-[#F7F7FD]" : ""
                  }`}
                  onClick={() =>
                    navigate(
                      `/session-overview/${student.id}?name=${name}&lastName=${lastName}&session=${student.date}`
                    )
                  }
                >
                  <Typography type="Body2">
                    {formatDate(student.date)}
                  </Typography>
                </td>
                <td
                  onMouseEnter={() => setHoveredRow(index)}
                  onMouseLeave={() => setHoveredRow(null)}
                  className={`p-24 ${
                    index !== validFeedbacks.length - 1
                      ? "border-b border-secondary"
                      : ""
                  } cursor-pointer transition-colors duration-200 ease-in ${
                    hovered === index ? "bg-[#F7F7FD]" : ""
                  }`}
                  onClick={() =>
                    navigate(
                      `/session-overview/${student.id}?name=${name}&lastName=${lastName}&session=${student.date}`
                    )
                  }
                >
                  <Typography type="Body2">
                    {`${
                      student.totalPracticeTime.days
                        ? `${student.totalPracticeTime.days} days, `
                        : ""
                    }${
                      student.totalPracticeTime.hours
                        ? `${student.totalPracticeTime.hours} hours, `
                        : ""
                    }${student.totalPracticeTime.minutes || 0} minutes`}
                  </Typography>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div ref={bottomRef} />
    </div>
  );
};

export default SessionOverviewList;
